/*
  What the documentation says.

  Kept as data rather than markup so the page renders it uniformly and so
  every claim sits in one place where it can be checked against the code.
  Nothing here is aspirational: each figure is either read live from the
  contract at runtime or was read off chain before being written down.
*/

export type DocBlock =
  | { kind: "text"; body: string }
  | { kind: "steps"; items: [string, string][] }
  | { kind: "facts"; items: [string, string][] }
  | { kind: "list"; items: string[] }
  | { kind: "note"; body: string };

export type DocSection = {
  id: string;
  title: string;
  lede: string;
  blocks: DocBlock[];
};

export const DOC_SECTIONS: DocSection[] = [
  {
    id: "what",
    title: "What Fons does",
    lede: "Two tokens go in. One new one comes out, built from what makes both of them work.",
    blocks: [
      {
        kind: "text",
        body:
          "Most tokens are made by copying something that already moved and changing a few letters. Fons takes a different route: you pick two tokens that already exist on Robinhood Chain, and it reads the character of each one and combines them into a single new concept. Not a name glued to a name — the logic underneath.",
      },
      {
        kind: "steps",
        items: [
          ["Pick two", "Search the live feed by name, ticker or contract address. The two have to be different."],
          ["Get three concepts", "The mix returns three, and marks the one it would choose. You pick before any image is made."],
          ["Generate one avatar", "One square image for the concept you chose."],
          ["Edit anything", "Name, ticker, description, links and image are all yours to change before a wallet ever opens."],
          ["Launch", "The transaction is built, checked and simulated, then sent. Fons pays the fee and gas, so there is nothing to sign in MetaMask."],
        ],
      },
      {
        kind: "note",
        body:
          "You can skip the AI entirely. Direct Launch takes your own name, ticker, description and image and goes straight to the chain. If every model Fons uses went down, that half still works.",
      },
    ],
  },
  {
    id: "ai",
    title: "What the AI produces",
    lede: "Exactly four things, and not one more.",
    blocks: [
      {
        kind: "facts",
        items: [
          ["Name", "2 to 32 characters"],
          ["Ticker", "1 to 6 letters or numbers"],
          ["Description", "1 to 500 characters"],
          ["Avatar", "one square 1024×1024 image"],
        ],
      },
      {
        kind: "text",
        body:
          "There is no thread generator, no roadmap, no tokenomics, no token site. Everything the model writes is editable, and nothing it produces can open your wallet or sign anything.",
      },
      {
        kind: "note",
        body:
          "If the text model is unavailable the mix still runs, on a deterministic fallback that is labelled as such on screen. Fons never presents a fallback as AI output.",
      },
    ],
  },
  {
    id: "chain",
    title: "The chain",
    lede: "Robinhood Chain, an Arbitrum Orbit L2. Gas is paid in ETH.",
    blocks: [
      {
        kind: "facts",
        items: [
          ["Network", "Robinhood Chain"],
          ["Chain ID", "4663"],
          ["Gas token", "ETH"],
          ["Wallet", "MetaMask"],
          ["Explorer", "robinhoodchain.blockscout.com"],
        ],
      },
      {
        kind: "text",
        body:
          "Fons never connects your wallet on its own. Nothing is requested until you press connect, and if you are on another network it offers to switch, or to add the network if your wallet does not know it yet.",
      },
      {
        kind: "note",
        body:
          "Every read goes through the Fons proxy rather than straight to a public node. The proxy carries reads only: no method that can send or sign a transaction is on its allowlist, so no setting can turn one on. Signing happens in your wallet and never touches the server.",
      },
    ],
  },
  {
    id: "cost",
    title: "What a launch costs",
    lede: "Fons pays the launch fee and gas by default. Launching is free.",
    blocks: [
      {
        kind: "facts",
        items: [
          ["Fons fee", "0 ETH"],
          ["Launch contract fee", "Covered by Fons"],
          ["Gas", "Covered by Fons"],
          ["What you pay", "0 ETH"],
        ],
      },
      {
        kind: "text",
        body:
          "By default, Fons signs and pays for the launch itself from its own wallet: no MetaMask signature and no debit from your wallet for the launch transaction. The launch still deploys two contracts, your token and its curve; that cost comes out of Fons's balance, not yours.",
      },
      {
        kind: "note",
        body:
          "Prefer to pay it yourself? Direct Launch still supports that: the launch contract fee is currently 0.0005 ETH and gas is about 0.0019 ETH, so a self-paid launch typically totals about 0.0021 ETH, read live from the chain and shown before you sign. Your wallet reserves the maximum it could cost and refunds the difference, so keep a small buffer on the account.",
      },
    ],
  },
  {
    id: "trading",
    title: "How the token trades",
    lede: "Every launch starts on its own bonding curve.",
    blocks: [
      {
        kind: "text",
        body:
          "The full supply is minted to the curve at launch. Earlier buyers pay less and the price rises with demand. When the curve fills, liquidity moves into a locked pool and the token trades like anything else. Nobody gets a private entrance, an allocation or a list.",
      },
      {
        kind: "note",
        body:
          "Until it graduates there is no chart on DexScreener, because there is no pool yet. That is why the success page sends you to the curve first.",
      },
    ],
  },
  {
    id: "buy",
    title: "The opening buy",
    lede: "Optional, off by default, and always a second signature.",
    blocks: [
      {
        kind: "text",
        body:
          "The launch contract requires the launch transaction to carry exactly the launch fee, so a purchase cannot ride along inside it. If you set an opening buy, Fons sends it as its own transaction against the curve, after the launch is confirmed and recorded.",
      },
      {
        kind: "list",
        items: [
          "Your token exists whether or not the second signature happens.",
          "Declining or skipping the buy never reads as a failed launch.",
          "The buy is simulated first and floored at the curve's own quoted amount minus 5%, so it reverts rather than filling at a price someone else moved.",
        ],
      },
    ],
  },
  {
    id: "safety",
    title: "What Fons checks before launching",
    lede: "In this order, every time — whether Fons is paying or you are.",
    blocks: [
      {
        kind: "steps",
        items: [
          ["The contract is real", "The launch contract address is checked for bytecode. No code, no launch."],
          ["The terms are current", "Fee and economics are read live and pinned, so a change between quoting and sending reverts instead of silently repricing."],
          ["The transaction simulates", "The whole launch is run against the chain first. A failed simulation stops it before anything is sent."],
          ["The cost is shown", "On a self-paid launch: fee, gas and any opening buy, with the maximum debit, before the wallet opens. On a sponsored launch: the same numbers, paid from Fons's wallet instead."],
        ],
      },
      {
        kind: "note",
        body:
          "If a send comes back unknown, Fons stops. It checks the chain, tells you what it found and waits for you. It will never quietly build a second token.",
      },
    ],
  },
  {
    id: "vault",
    title: "The rewards vault",
    lede: "Fees from sponsored launches, split across $FONS holders.",
    blocks: [
      {
        kind: "text",
        body:
          "When Fons pays for a launch it is the on-chain creator, so the creator fee on that token's trades belongs to Fons rather than to any individual. That fee is pointed at one published wallet - the vault - and its balance is split across $FONS holders in proportion to how much they hold.",
      },
      {
        kind: "steps",
        items: [
          ["Fons launches and pays", "On a sponsored launch the creator fee is Fons's, because Fons is the creator the factory records."],
          ["The fee lands in the vault", "One wallet, address published on the vault page, balance visible to anyone."],
          ["Holders are paid their share", "Balances are read at a single block; each holder gets a proportional share, and rounding stays in the vault."],
        ],
      },
      {
        kind: "note",
        body:
          "Payouts are sent by Fons from that wallet rather than enforced by a contract, so they depend on Fons actually sending them. That is why the address is published: every payment can be checked on the explorer. Fees only exist if people trade, and no rate is promised.",
      },
      {
        kind: "list",
        items: [
          "Shares are computed from balances at one block, so buying in after a snapshot does not backdate a claim.",
          "Amounts smaller than the gas needed to send them are skipped and stay in the vault for the next distribution.",
          "The vault's own balance is excluded from the split, so it cannot pay itself.",
        ],
      },
    ],
  },
  {
    id: "honesty",
    title: "What Fons will not tell you",
    lede: "The gaps are on purpose.",
    blocks: [
      {
        kind: "list",
        items: [
          "When a value cannot be read, the field says unknown. There is no invented volume and no placeholder holder count.",
          "A similar-name check is informational. It never blocks a launch and it is not a trademark search.",
          "Fons never asks for or holds your wallet's keys or funds, on either launch path. On a sponsored launch Fons's own wallet is the one that calls the launch contract, so it is the on-chain deployer of record for that token; on a self-paid launch, yours is.",
          "Nothing here is a promise of price, liquidity, a listing or a market. Token creation and trading are risky.",
        ],
      },
    ],
  },
];

export const DOC_META = {
  eyebrow: "Documentation",
  title: ["How Fons works"],
  lede: "Everything the product actually does, in the order you meet it.",
};
